import { CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField as MuiTextField } from "@material-ui/core";
import { Archive, Cancel, Visibility } from "@material-ui/icons";
import RichTextInput from "ra-input-rich-text";
import { useState } from "react";
import {
    ArrayField,
    ArrayInput,
    BooleanField,
    Button, ChipField, Datagrid, Edit,
    FormDataConsumer,
    FormTab,
    ReferenceManyField, SimpleFormIterator,
    SingleFieldList,
    TabbedForm, TextField, TextInput,
    TopToolbar, required, useNotify, useRedirect, useRefresh
} from "react-admin";
import AddNewAttachmentButton from "../../components/add-new-attachment-button";
import AttachmentDeleteButton from "../../components/attachment-delete-button";
import { Spacer } from "../../components/spacer";
import { VideoUrlInput } from "../../components/video-url-input";
import { ViewFileButton } from "../../components/view-file-button";
import dataProvider from "../../providers/data-provider";
import { videoIdExtractorTransform } from "../../utils/video-id-extractor-transform";
import SchoolActivityEditAddPartyType from "./school-activities-add-party-type";


const WarningDialog = ({ open, onClose, onAccept, ...props }) => {
    const [loading, setLoading] = useState(false)
    const [val, setVal] = useState('')
    const [valid, setValid] = useState(false)
    const handleClick = () => { setLoading(true); onAccept() }
    const onChange = (event) => {
        const newVal = event.target.value.toUpperCase();
        if (newVal === 'FINALIZAR') {
            setValid(true)
        } else {
            setValid(false)
        }; setVal(newVal)
    }
    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>{`Aviso!`}</DialogTitle>
            <DialogContent>
                <DialogContentText>{`Ao finalizar esta atividade prática, esta será tida como finalizada e todas as movimentações de todos os seus processos serão marcadas como corrigidas e seus prazos não serão mais rastreados no dashboard do professor.`}</DialogContentText>
                <DialogContentText>{`Para prosseguir com esta ação, digite "FINALIZAR" no campo de texto abaixo.`}</DialogContentText>
                <MuiTextField variant="filled" label={'Confirmação'} value={val} onChange={onChange} fullWidth />
            </DialogContent>
            <DialogActions>
                <Button label="Cancelar" variant="contained" onClick={onClose} disabled={loading}><Cancel /></Button>
                <Spacer />
                <Button label="Prosseguir" variant="outlined" onClick={handleClick} disabled={loading || !valid}>{loading ? <CircularProgress size={16} /> : <Archive />}</Button>
            </DialogActions>
        </Dialog>
    )
}

const SchoolActivityEdit = (props) => {

    const refresh = useRefresh();
    const redirect = useRedirect();
    const notify = useNotify();
    const [warningOpen, setWarningOpen] = useState(false)

    const { tab } = props.history.location.state ? props.history.location.state : { tab: false };

    const ToolbarEdit = ({ basePath, data }) => (
        <TopToolbar>
            {!data?.has_finished && <Button onClick={handleDialogOpen} label={'Finalizar atividade prática'}>
                <Archive />
            </Button>
            }
            <WarningDialog open={warningOpen} onClose={handleDialogClose} onAccept={handleFinalizeActivity} />
            <Spacer />
            <Button label={'Dashboard'} onClick={() => redirect(`/school-activity-dashboard/${data.id}`)}>
                <Visibility />
            </Button>
        </TopToolbar>
    )

    const CustomTitle = ({ record }) => {
        return <span>{record ? `Atividade prática - ${record.name} - ${record.class.name}` : ''}</span>
    }

    const transform = async (data) => {
        let newData = data;
        data = videoIdExtractorTransform(newData)
        return { ...newData }
    }

    const handleDialogOpen = () => {
        setWarningOpen(true)
    }

    const handleDialogClose = () => setWarningOpen(false)

    const handleFinalizeActivity = () => {
        dataProvider.post('school-activities', `${props.id}/finish`, { has_finished: false })
            .then(() => { notify('Atividade finalizada com sucesso', 'success'); refresh() })
            .catch(() => { notify('Erro ao finalizar atividade', 'error') })
            .finally(() => handleDialogClose())
    }

    return (
        <Edit
            mutationMode="pessimistic"
            actions={<ToolbarEdit />}
            title={<CustomTitle />}
            onSuccess={(record) => {
                notify('Registro alterado com sucesso!', 'success')
                redirect(`/school-activity-dashboard/${record.data.id}`)
            }}
            transform={transform}
            {...props}
        >
            <TabbedForm>
                {!tab &&
                    <FormTab label='Dados Gerais'>
                        <TextInput source='name' label='Título' fullWidth />

                        <ArrayField source="themes" label={'Áreas do direito'}>
                            <SingleFieldList linkType={false}>
                                <ChipField source="name" clickable={false} />
                            </SingleFieldList>
                        </ArrayField>

                        <TextInput source='author' label='Autor' disabled fullWidth />

                        <ArrayField source="teachers" label={'Professores Responsáveis'}>
                            <SingleFieldList linkType={false}>
                                <ChipField source="user.name" clickable={false} />
                            </SingleFieldList>
                        </ArrayField>

                        <BooleanField source="has_started" label={"Iniciada?"} />
                        <BooleanField source="has_finished" label={"Finalizada?"} />

                    </FormTab>}

                {(!tab || tab === 'proposal') &&
                    <FormTab label={'Proposta de ensino'} path="proposal">
                        <RichTextInput source='proposal' label='Proposta de ensino/aprendizagem' />
                    </FormTab>}

                {(!tab || tab === 'prerequisites') &&
                    <FormTab label={'Pré-requisitos do aluno'} path="prerequisites">
                        <RichTextInput source='prerequisites' label='Pré-requisitos do aluno' />
                    </FormTab>}

                {(!tab || tab === 'guidelines') &&
                    <FormTab label={'Gabarito do professor'} path="guidelines">
                        <RichTextInput source='guidelines' label='Orientações' />
                        <ReferenceManyField fullWidth reference='attachments' resource='school-activity-guidelines' target='schoolActivityGuidelineId' label={'Anexos'}>
                            <Datagrid>
                                <TextField source={'name'} label={'Nome'} />
                                <ViewFileButton />
                                <FormDataConsumer>
                                    {({ formData, record }) =>
                                        <AttachmentDeleteButton path='school-activity-guidelines' resource={'attachments'} relatedId={formData.id} record={record} undoable={false} redirect={false} />
                                    }
                                </FormDataConsumer>
                            </Datagrid>
                        </ReferenceManyField>
                        <FormDataConsumer>
                            {({ record }) =>
                                <AddNewAttachmentButton record={record} resource={'school-activity-guidelines'} redirectTo={`/school-activities/${record.id}/guidelines`} fileSize={25000000} multiple={true} />
                            }
                        </FormDataConsumer>
                    </FormTab>}

                {(!tab || tab === 'details') &&
                    <FormTab label={'Orientações gerais'} path="details">
                        <RichTextInput source='details' label='Orientações gerais' />
                        <ArrayInput source="links" label="Vídeos" variant="standard">
                            <SimpleFormIterator>
                                <TextInput source="title" label="Título" validate={[required()]} fullWidth />
                                <VideoUrlInput source="url" label="URL" validate={[required()]} fullWidth />
                            </SimpleFormIterator>
                        </ArrayInput>
                    </FormTab>}

                {(!tab || tab === 'parties') &&
                    <FormTab label={'Sujeitos processuais'} path="parties">
                        <ReferenceManyField fullWidth reference="party-types" target="school_activity.id" label={'Sujeitos processuais representados pelo aluno'}>
                            <Datagrid>
                                <TextField source='name' label={'Nome'} />
                            </Datagrid>
                        </ReferenceManyField>
                        <FormDataConsumer>
                            {
                                ({ formData, record, ...rest }) => {
                                    return <SchoolActivityEditAddPartyType record={record} refresh={refresh} />
                                }
                            }
                        </FormDataConsumer>
                    </FormTab>}

                {(!tab || tab === 'attachments') &&
                    <FormTab label="Anexos" path="attachments">
                        <ReferenceManyField fullWidth reference='attachments' target='schoolActivityId' label={'Anexos'}>
                            <Datagrid>
                                <TextField source={'name'} label={'Nome'} />
                                <ViewFileButton />
                                <FormDataConsumer>
                                    {({ formData, record }) =>
                                        <AttachmentDeleteButton path='school-activities' resource={'attachments'} relatedId={formData.id} record={record} undoable={false} redirect={false} />
                                    }
                                </FormDataConsumer>
                            </Datagrid>
                        </ReferenceManyField>
                        <AddNewAttachmentButton fileSize={25000000} multiple={true} />
                    </FormTab>}
            </TabbedForm>
        </Edit >
    )
}

export default SchoolActivityEdit 